/**
 * VideoProgressModal.jsx
 * Öğrencinin kurs bazında ders izleme ilerlemesini gösterir.
 * Props: studentUserId, courseId (opsiyonel), open, onClose
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Video, CheckCircle2, Clock } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatRelativeTime, formatAbsoluteDateTime, toIsoString } from '../../utils/relativeTime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Progress bar
function ProgressBar({ percent }) {
  const clamped = Math.min(100, Math.max(0, percent || 0));
  const color =
    clamped >= 100
      ? 'bg-emerald-500'
      : clamped >= 50
      ? 'bg-cyan-500'
      : 'bg-amber-400';
  return (
    <div className="flex items-center gap-2 min-w-0 flex-1">
      <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${clamped}%` }}
        />
      </div>
      <span className="text-[11px] font-bold text-[var(--text-muted)] w-8 text-right shrink-0">
        {clamped}%
      </span>
    </div>
  );
}

// Skeleton loader
function LessonSkeleton() {
  return (
    <div className="space-y-2 animate-pulse">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 p-3 rounded-xl border-2 border-[var(--border-light)]"
        >
          <div className="w-5 h-5 bg-gray-200 rounded-full shrink-0" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 bg-gray-200 rounded w-3/5" />
            <div className="h-2 bg-gray-100 rounded w-full" />
          </div>
          <div className="h-4 bg-gray-100 rounded w-14 shrink-0" />
        </div>
      ))}
    </div>
  );
}

export default function VideoProgressModal({ studentUserId, courseId, open, onClose }) {
  const { t, lang } = useLanguage();
  const { authHeaders } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProgress = useCallback(async () => {
    if (!studentUserId) return;
    setLoading(true);
    setError(null);
    try {
      const params = courseId ? `?course_id=${courseId}` : '';
      const r = await axios.get(
        `${API}/admin/students/${studentUserId}/video-progress${params}`,
        { headers: authHeaders() }
      );
      setData(r.data);
    } catch {
      setError(t('admin.studentHub.videoProgress.error'));
    } finally {
      setLoading(false);
    }
  }, [studentUserId, courseId, authHeaders, t]);

  useEffect(() => {
    if (open) fetchProgress();
    else {
      setData(null);
      setError(null);
    }
  }, [open, fetchProgress]);

  // Derive lesson list — flatten courses or use single course
  const courses = data?.courses || [];
  const activeCourse = courseId
    ? courses.find((c) => c.course_id === courseId) || courses[0]
    : courses[0];
  const lessons = activeCourse?.lessons || [];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="video-progress-modal-backdrop"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="bg-white rounded-2xl border-2 border-[var(--border-light)] p-0 max-w-lg w-full shadow-2xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="video-progress-modal"
          >
            {/* Header */}
            <div className="flex items-start justify-between p-5 border-b-2 border-[var(--border-light)] shrink-0">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <Video size={18} className="text-[var(--brand-primary)] shrink-0" />
                  <h3 className="font-display text-lg font-bold text-[var(--text-main)] truncate">
                    {t('admin.studentHub.videoProgress.title')}
                  </h3>
                </div>
                {data?.student && (
                  <p className="text-xs text-[var(--text-muted)] truncate ml-6">
                    {data.student.name}
                    {activeCourse?.course_title && (
                      <> &middot; <span className="font-semibold">{activeCourse.course_title}</span></>
                    )}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="ml-3 p-1.5 rounded-lg hover:bg-gray-100 shrink-0"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1 p-5">
              {loading && <LessonSkeleton />}

              {!loading && error && (
                <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
                  <span className="text-sm text-rose-500 font-semibold">{error}</span>
                  <button
                    onClick={fetchProgress}
                    className="px-4 py-2 rounded-xl border-2 border-[var(--brand-primary)] text-[var(--brand-primary)] text-xs font-bold hover:bg-cyan-50"
                  >
                    {lang === 'tr' ? 'Tekrar Dene' : 'Retry'}
                  </button>
                </div>
              )}

              {!loading && !error && data && lessons.length === 0 && (
                <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
                  <Video size={36} strokeWidth={1.5} className="text-gray-300" />
                  <p className="text-sm text-[var(--text-muted)] font-medium">
                    {t('admin.studentHub.videoProgress.empty')}
                  </p>
                </div>
              )}

              {!loading && !error && lessons.length > 0 && (
                <>
                  {/* Column headers */}
                  <div className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 mb-2">
                    <span className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wider">
                      {t('admin.studentHub.videoProgress.lessonHeader')}
                    </span>
                    <span className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wider w-28 text-right">
                      {t('admin.studentHub.videoProgress.percentHeader')}
                    </span>
                    <span className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wider w-20 text-right">
                      {t('admin.studentHub.videoProgress.lastWatchedHeader')}
                    </span>
                  </div>

                  <div className="space-y-1.5">
                    {lessons.map((lesson, i) => (
                      <div
                        key={lesson.lesson_id || i}
                        className="grid grid-cols-[1fr_auto_auto] items-center gap-2 p-3 rounded-xl border-2 border-[var(--border-light)] hover:bg-gray-50 transition-colors"
                        data-testid={`lesson-row-${i}`}
                      >
                        {/* Lesson title + completed indicator */}
                        <div className="flex items-center gap-2 min-w-0">
                          {lesson.completed ? (
                            <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
                          ) : (
                            <div className="w-4 h-4 rounded-full border-2 border-gray-300 shrink-0" />
                          )}
                          <span className="text-xs font-semibold text-[var(--text-main)] truncate">
                            {lesson.lesson_title || lesson.lesson_id}
                          </span>
                        </div>

                        {/* Progress bar */}
                        <div className="w-28">
                          {lesson.watch_percent > 0 ? (
                            <ProgressBar percent={lesson.watch_percent} />
                          ) : (
                            <span className="text-[10px] text-gray-400 font-medium">
                              {t('admin.studentHub.videoProgress.notStarted')}
                            </span>
                          )}
                        </div>

                        {/* Last watched — dar sütunda kısa form (dk/sa/g),
                            tam metin aria-label + title'da kalır. */}
                        <div className="w-20 text-right">
                          {lesson.updated_at ? (
                            <time
                              dateTime={toIsoString(lesson.updated_at)}
                              title={formatAbsoluteDateTime(lesson.updated_at, lang)}
                              aria-label={formatRelativeTime(lesson.updated_at, lang)}
                              className="flex items-center justify-end gap-0.5 text-[10px] text-[var(--text-muted)] whitespace-nowrap"
                            >
                              <Clock size={10} />
                              {formatRelativeTime(lesson.updated_at, lang, { short: true })}
                            </time>
                          ) : (
                            <span className="text-[10px] text-gray-300">—</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Footer summary */}
            {!loading && !error && data && (
              <div className="px-5 py-3 border-t-2 border-[var(--border-light)] bg-gray-50 shrink-0">
                <p className="text-xs text-[var(--text-muted)] font-semibold text-center">
                  {t('admin.studentHub.videoProgress.summary')
                    .replace('{{completed}}', String(data.completed_lessons ?? 0))
                    .replace('{{total}}', String(data.total_lessons ?? 0))
                    .replace('{{avg}}', String(Math.round(data.avg_watch_percent ?? 0)))}
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
